import type { ComponentProps } from 'react';

import type { ColorName, ColorVariant, Style } from './buttonClasses';
import type { BaseProps, Size, UpdateStatus } from './types';
import { cn } from '../cn';
import { composeVariant, inputVariantClasses, inputVariantPlaceholder } from './buttonClasses';
import {
  shapeRounded,
  sizeFont,
  sizeHeight,
  sizeIconClass,
  sizeIconPaddingL,
  sizeIconPaddingR,
  sizeInnerGap,
  sizePaddingL,
  sizePaddingR,
} from './types';
import UpdateStatusIcon from './UpdateStatusIcon';

export type InputProps = Omit<ComponentProps<'input'>, 'size'> &
  BaseProps & {
    size?: Size;
    /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
    color?: ColorName;
    /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
    tone?: Style;
    /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
    status?: UpdateStatus;
  };

export default function Input({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  className,
  disabled,
  readOnly,
  status,
  ...props
}: InputProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const hasError = error != null && error !== '';
  const inputRow = (
    <div
      className={cn(
        'inline-flex items-center font-sans transition-colors',
        sizeHeight(size),
        shapeRounded(shape, size),
        sizeFont(size),
        sizeInnerGap(size),
        !icon && !status && sizePaddingL(size),
        !rIcon && sizePaddingR(size),
        'border',
        inputVariantClasses(effectiveVariant, bordered, !!error, !!disabled, !!readOnly),
        className,
      )}>
      {(icon || status) && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingL(size))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>
            {status != null && <UpdateStatusIcon status={status} />}
            {!status && icon}
          </span>
        </span>
      )}
      <input
        disabled={disabled}
        readOnly={readOnly}
        className={cn(
          'h-full flex-1 min-w-0 bg-transparent outline-hidden border-none',
          inputVariantPlaceholder(effectiveVariant, !!error),
          disabled && 'cursor-not-allowed',
          readOnly && 'cursor-default',
        )}
        {...props}
      />
      {rIcon && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingR(size))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{rIcon}</span>
        </span>
      )}
    </div>
  );
  if (!hasError) return inputRow;
  return (
    <div className="inline-flex flex-col">
      {inputRow}
      <div className="text-sm leading-5 font-normal text-fair-pink-600 dark:text-fair-pink-400 px-3 py-0.5">
        {error}
      </div>
    </div>
  );
}
